import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { Directory, StrategyConfig, StrategyLevel, StrategyNode, StrategyNodeCreate, StrategyNodeUpdate, StrategyStatus } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Field, Modal, SearchableSelect } from '../../components/ui';
import { STATUSES, useLevelLabel } from './shared';

type Form = {
  code: string;
  name_en: string;
  name_ar: string;
  description_en: string;
  description_ar: string;
  vision_en: string;
  vision_ar: string;
  mission_en: string;
  mission_ar: string;
  status: StrategyStatus;
  status_override: boolean;
  status_reason: string;
  start_date: string;
  end_date: string;
  owner_id: string;
  sort_order: number;
  onlyMe: boolean;
};

const empty: Form = { code: '', name_en: '', name_ar: '', description_en: '', description_ar: '', vision_en: '', vision_ar: '', mission_en: '', mission_ar: '', status: 'planned', status_override: false, status_reason: '', start_date: '', end_date: '', owner_id: '', sort_order: 0, onlyMe: false };

/** Create (with `level` + `parentId`) or edit (with `node`) a cascade node; the same form for every level. */
export function NodeForm({ open, onClose, config, level, parentId, node, onSaved }: { open: boolean; onClose: () => void; config: StrategyConfig | undefined; level: StrategyLevel; parentId?: string | null; node?: StrategyNode | null; onSaved?: (n: StrategyNode) => void }) {
  const { t, i18n } = useTranslation();
  const { canAction } = useAuth();
  const qc = useQueryClient();
  const levelLabel = useLevelLabel(config);
  const directory = useQuery({ queryKey: ['users', 'directory'], queryFn: () => api.get<Directory>('/api/users/directory'), staleTime: 60_000, enabled: open });
  const [form, setForm] = useState<Form>(empty);
  const isNew = !node;
  const isLeaf = config?.leaf === level;
  const canOverride = canAction('strategy.override_status');

  useEffect(() => {
    if (!open) return;
    if (node) {
      setForm({ code: node.code, name_en: node.name_en, name_ar: node.name_ar, description_en: node.description_en ?? '', description_ar: node.description_ar ?? '', vision_en: node.vision_en ?? '', vision_ar: node.vision_ar ?? '', mission_en: node.mission_en ?? '', mission_ar: node.mission_ar ?? '', status: node.status, status_override: node.status_override, status_reason: node.status_reason ?? '', start_date: node.start_date ?? '', end_date: node.end_date ?? '', owner_id: node.owner?.id ?? '', sort_order: node.sort_order, onlyMe: false });
    } else {
      setForm(empty);
    }
  }, [open, node]);

  const ownerOptions = useMemo(() => (directory.data?.items ?? []).filter((u) => u.is_active).map((u) => ({ value: u.id, label: pick(i18n.language, u.display_name_en, u.display_name_ar) })), [directory.data, i18n.language]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['strategy'] });
  const create = useMutation({
    mutationFn: (b: StrategyNodeCreate) => api.post<StrategyNode>('/api/strategy/nodes', b),
    onSuccess: (n) => { toast.success(t('strategy.saved')); void invalidate(); onClose(); onSaved?.(n); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const update = useMutation({
    mutationFn: (b: StrategyNodeUpdate) => api.patch<StrategyNode>(`/api/strategy/nodes/${node!.id}`, b),
    onSuccess: (n) => { toast.success(t('strategy.saved')); void invalidate(); onClose(); onSaved?.(n); },
    onError: (e) => toast.error(describeError(e, t)),
  });

  const submit = () => {
    const base = {
      code: form.code.trim() || null,
      name_en: form.name_en.trim(),
      name_ar: form.name_ar.trim(),
      description_en: form.description_en || null,
      description_ar: form.description_ar || null,
      vision_en: form.vision_en || null,
      vision_ar: form.vision_ar || null,
      mission_en: form.mission_en || null,
      mission_ar: form.mission_ar || null,
      status: form.status,
      status_override: !isLeaf && form.status_override,
      status_reason: form.status_reason || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      owner_id: form.owner_id || null,
      sort_order: Number(form.sort_order) || 0,
    };
    if (isNew) create.mutate({ ...base, level, parent_id: parentId ?? null, ...(form.onlyMe ? { visibility: 'private' as const } : {}) });
    else update.mutate(base);
  };
  const valid = form.name_en.trim() && form.name_ar.trim() && (!form.status_override || form.status_reason.trim());

  return (
    <Modal open={open} onClose={onClose} title={isNew ? t('strategy.newChild', { level: levelLabel(level) }) : t('strategy.editNode', { level: levelLabel(level) })} wide footer={<><button type="button" className="btn-secondary" onClick={onClose}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={!valid || create.isPending || update.isPending} onClick={submit}>{t('app.save')}</button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('strategy.nameEn')} required><input className="input" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></Field>
        <Field label={t('strategy.nameAr')} required><input className="input" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></Field>
        <Field label={t('strategy.code')} hint={isNew ? t('strategy.codeHint') : undefined}><input className="input font-mono" dir="ltr" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></Field>
        <Field label={t('strategy.owner')}><SearchableSelect value={form.owner_id} onChange={(v) => setForm({ ...form, owner_id: v as string })} options={[{ value: '', label: t('app.none') }, ...ownerOptions]} /></Field>
        <Field label={t('strategy.descriptionEn')}><textarea className="input min-h-20" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} /></Field>
        <Field label={t('strategy.descriptionAr')}><textarea className="input min-h-20" dir="rtl" value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} /></Field>
        {level === 'strategy' && (
          <>
            <Field label={t('strategy.visionEn')}><textarea className="input min-h-16" value={form.vision_en} onChange={(e) => setForm({ ...form, vision_en: e.target.value })} /></Field>
            <Field label={t('strategy.visionAr')}><textarea className="input min-h-16" dir="rtl" value={form.vision_ar} onChange={(e) => setForm({ ...form, vision_ar: e.target.value })} /></Field>
            <Field label={t('strategy.missionEn')}><textarea className="input min-h-16" value={form.mission_en} onChange={(e) => setForm({ ...form, mission_en: e.target.value })} /></Field>
            <Field label={t('strategy.missionAr')}><textarea className="input min-h-16" dir="rtl" value={form.mission_ar} onChange={(e) => setForm({ ...form, mission_ar: e.target.value })} /></Field>
          </>
        )}
        <Field label={t('strategy.startDate')}><input className="input" type="date" dir="ltr" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
        <Field label={t('strategy.endDate')}><input className="input" type="date" dir="ltr" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
        <Field label={t('strategy.status')} hint={!isLeaf && !form.status_override ? t('strategy.derivedHint') : undefined}>
          <SearchableSelect value={form.status} onChange={(v) => setForm({ ...form, status: v as StrategyStatus })} options={STATUSES.map((s) => ({ value: s, label: t(`strategy.statuses.${s}`) }))} disabled={!isLeaf && !form.status_override} />
        </Field>
        <Field label={t('strategy.sortOrder')}><input className="input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></Field>
        {!isLeaf && (
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={!canOverride && !form.status_override} checked={form.status_override} onChange={(e) => setForm({ ...form, status_override: e.target.checked })} />{t('strategy.overrideStatus')} <span className="text-xs text-neutral-500">{t('strategy.overrideHint')}</span></label>
            {form.status_override && <Field label={t('strategy.statusReason')} required><input className="input" dir="auto" value={form.status_reason} onChange={(e) => setForm({ ...form, status_reason: e.target.value })} /></Field>}
          </div>
        )}
        {isNew && <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.onlyMe} onChange={(e) => setForm({ ...form, onlyMe: e.target.checked })} />{t('records.onlyMe')} <span className="text-xs text-neutral-500">{t('strategy.onlyMeHint')}</span></label>}
      </div>
    </Modal>
  );
}
